import * as THREE from 'three';
import * as CANNON from 'cannon-es';

export class Stadium {
  constructor(scene, physicsWorld) {
    this.scene = scene;
    this.physicsWorld = physicsWorld;
    
    // Stadium dimensions - significantly increased
    this.dimensions = {
      width: 100,    // Increased from 80
      length: 160,   // Increased from 120
      wallHeight: 30, // Increased from 20
      goalWidth: 30,  // Increased from 20
      goalHeight: 15, // Increased from 10
      goalDepth: 10   // Increased from 5
    };
    
    // Create stadium components
    this.createStadium();
  }
  
  createStadium() {
    this.createField();
    this.createWalls();
    this.createGoals();
  }
  
  createField() {
    // Create field floor
    const floorGeometry = new THREE.BoxGeometry(
      this.dimensions.length,
      1,
      this.dimensions.width
    );
    
    const floorMaterial = new THREE.MeshPhongMaterial({
      color: 0x0a8c21,
      shininess: 0
    });
    
    // Add field lines as texture
    this.addFieldLines(floorMaterial);
    
    this.floorMesh = new THREE.Mesh(floorGeometry, floorMaterial);
    this.floorMesh.receiveShadow = true;
    this.floorMesh.position.set(0, -0.5, 0);
    
    // Add to scene
    this.scene.add(this.floorMesh);
    
    // Create floor physics body
    this.floorBody = this.physicsWorld.createBoxBody(
      { x: this.dimensions.length, y: 1, z: this.dimensions.width },
      { x: 0, y: -0.5, z: 0 }
    );
  }
  
  addFieldLines(material) {
    // Create a canvas for the field texture
    const canvasSize = 1024;
    const canvas = document.createElement('canvas');
    canvas.width = canvasSize;
    canvas.height = canvasSize;
    
    const ctx = canvas.getContext('2d');
    
    // Fill with green
    ctx.fillStyle = '#0a8c21';
    ctx.fillRect(0, 0, canvasSize, canvasSize);
    
    // Draw field lines
    ctx.strokeStyle = 'white';
    ctx.lineWidth = 8;
    
    // Outer boundary
    const margin = 50;
    ctx.strokeRect(margin, margin, canvasSize - 2 * margin, canvasSize - 2 * margin);
    
    // Center line
    ctx.beginPath();
    ctx.moveTo(canvasSize / 2, margin);
    ctx.lineTo(canvasSize / 2, canvasSize - margin);
    ctx.stroke();
    
    // Center circle
    ctx.beginPath();
    ctx.arc(canvasSize / 2, canvasSize / 2, 100, 0, Math.PI * 2);
    ctx.stroke();
    
    // Create texture
    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(1, 1);
    
    material.map = texture;
    material.needsUpdate = true;
  }
  
  createWalls() {
    // Wall material
    const wallMaterial = new THREE.MeshPhongMaterial({
      color: 0xaaaaaa,
      transparent: true,
      opacity: 0.3
    });
    
    // Create walls
    const walls = [
      // Left wall
      {
        size: [1, this.dimensions.wallHeight, this.dimensions.width],
        position: [-this.dimensions.length / 2, this.dimensions.wallHeight / 2, 0],
        holes: [{ width: this.dimensions.goalWidth, height: this.dimensions.goalHeight }]
      },
      // Right wall
      {
        size: [1, this.dimensions.wallHeight, this.dimensions.width],
        position: [this.dimensions.length / 2, this.dimensions.wallHeight / 2, 0],
        holes: [{ width: this.dimensions.goalWidth, height: this.dimensions.goalHeight }]
      },
      // Front wall
      {
        size: [this.dimensions.length, this.dimensions.wallHeight, 1],
        position: [0, this.dimensions.wallHeight / 2, this.dimensions.width / 2],
        holes: []
      },
      // Back wall
      {
        size: [this.dimensions.length, this.dimensions.wallHeight, 1],
        position: [0, this.dimensions.wallHeight / 2, -this.dimensions.width / 2],
        holes: []
      },
      // Ceiling
      {
        size: [this.dimensions.length, 1, this.dimensions.width],
        position: [0, this.dimensions.wallHeight, 0],
        holes: []
      }
    ];
    
    // Create each wall
    this.wallBodies = [];
    
    walls.forEach(wall => {
      // Simple wall creation - in a real implementation, handle holes for goals
      const wallGeometry = new THREE.BoxGeometry(wall.size[0], wall.size[1], wall.size[2]);
      const wallMesh = new THREE.Mesh(wallGeometry, wallMaterial);
      
      wallMesh.position.set(wall.position[0], wall.position[1], wall.position[2]);
      wallMesh.receiveShadow = true;
      
      this.scene.add(wallMesh);
      
      // Create wall physics body with wall material for better bouncing
      const wallBody = this.physicsWorld.createBoxBody(
        { x: wall.size[0], y: wall.size[1], z: wall.size[2] },
        { x: wall.position[0], y: wall.position[1], z: wall.position[2] },
        { 
          material: this.physicsWorld.wallMaterial, // Use wall material
          mass: 0 // Static body
        }
      );
      
      // Explicitly ensure collision response is enabled
      wallBody.collisionResponse = true;
      
      // Set high friction and restitution for better collision behavior
      wallBody.material = this.physicsWorld.wallMaterial;
      
      this.wallBodies.push(wallBody);
    });
  }
  
  createGoals() {
    // Goal dimensions
    const { goalWidth, goalHeight, goalDepth } = this.dimensions;
    
    // Create the two goals
    const goals = [
      { team: 'blue', position: [-this.dimensions.length / 2 - goalDepth / 2, goalHeight / 2, 0] },
      { team: 'orange', position: [this.dimensions.length / 2 + goalDepth / 2, goalHeight / 2, 0] }
    ];
    
    goals.forEach(goal => {
      const color = goal.team === 'blue' ? 0x0066ff : 0xff6600;
      
      // Create goal mesh
      const goalGeometry = new THREE.BoxGeometry(goalDepth, goalHeight, goalWidth);
      const goalMaterial = new THREE.MeshPhongMaterial({
        color,
        transparent: true,
        opacity: 0.3
      });
      
      // Make the goal hollow
      goalGeometry.groups.forEach((group, index) => {
        if (index === 0) { // Keep only the front face
          group.materialIndex = 0;
        } else {
          group.materialIndex = 1;
        }
      });
      
      const hollowMaterial = new THREE.MeshPhongMaterial({
        color,
        transparent: true,
        opacity: 0.3,
        side: THREE.BackSide
      });
      
      const goalMesh = new THREE.Mesh(goalGeometry, [goalMaterial, hollowMaterial]);
      goalMesh.position.set(goal.position[0], goal.position[1], goal.position[2]);
      
      this.scene.add(goalMesh);
      
      // Create goal physics bodies - just the frame, not the hollow part
      // Top bar
      const topBar = this.physicsWorld.createBoxBody(
        { x: goalDepth, y: 1, z: goalWidth },
        { x: goal.position[0], y: goalHeight, z: goal.position[2] },
        { material: this.physicsWorld.wallMaterial }
      );
      // Ensure collision response is enabled
      topBar.collisionResponse = true;
      
      // Side posts
      const leftPost = this.physicsWorld.createBoxBody(
        { x: goalDepth, y: goalHeight, z: 1 },
        { x: goal.position[0], y: goalHeight / 2, z: goal.position[2] + goalWidth / 2 },
        { material: this.physicsWorld.wallMaterial }
      );
      leftPost.collisionResponse = true;
      
      const rightPost = this.physicsWorld.createBoxBody(
        { x: goalDepth, y: goalHeight, z: 1 },
        { x: goal.position[0], y: goalHeight / 2, z: goal.position[2] - goalWidth / 2 },
        { material: this.physicsWorld.wallMaterial }
      );
      rightPost.collisionResponse = true;
      
      // Back wall of goal - thin physics body for goal detection, but cars can go through
      if (goal.team === 'blue') {
        const backWall = this.physicsWorld.createBoxBody(
          { x: 0.1, y: goalHeight, z: goalWidth },
          { x: goal.position[0] - goalDepth / 2, y: goalHeight / 2, z: goal.position[2] }
        );
        // Make this a sensor - only detects collisions but doesn't physically block objects
        backWall.collisionResponse = false;
      } else {
        const backWall = this.physicsWorld.createBoxBody(
          { x: 0.1, y: goalHeight, z: goalWidth },
          { x: goal.position[0] + goalDepth / 2, y: goalHeight / 2, z: goal.position[2] }
        );
        // Make this a sensor - only detects collisions but doesn't physically block objects
        backWall.collisionResponse = false;
      }
    });
  }
} 